import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronRight, Calendar, Users, Layers, ChevronLeft } from 'react-feather';
import { listSegmentationRuns, type SegmentationRunSummary } from '../api/client';

const ITEMS_PER_PAGE = 5;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  max-width: 480px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4px;
`;

const Title = styled.h2`
  font-size: 17px;
  font-weight: 600;
  color: #323232;
  margin: 0 0 6px 0;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #626262;
  margin: 0;
  line-height: 1.4;
`;

const RunsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
`;

const RunCard = styled.button`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    border-color: #8B5CF6;
    background: #F9F5FF;
    transform: translateX(2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const RunIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ffffff;
`;

const RunInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const RunName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
  margin-bottom: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RunMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #626262;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const ChevronIcon = styled(ChevronRight)`
  color: #8B5CF6;
  flex-shrink: 0;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: #ADADAD;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #ADADAD;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #626262;
  margin-bottom: 8px;
`;

const EmptyText = styled.div`
  font-size: 14px;
  color: #ADADAD;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 13px;
  color: #626262;
`;

const PaginationInfo = styled.div`
  font-weight: 500;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const PaginationButton = styled.button`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #626262;

  &:hover:not(:disabled) {
    border-color: #8B5CF6;
    color: #8B5CF6;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SegmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const SegmentCard = styled.button`
  background: #F9F5FF;
  border: 2px solid #E9D5FF;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    border-color: #8B5CF6;
    background: #F3E8FF;
  }
`;

const SegmentName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
`;

const SegmentSize = styled.div`
  font-size: 13px;
  color: #626262;
`;

const BackButton = styled.button`
  background: none;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 8px 16px;
  color: #626262;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  align-self: flex-start;

  &:hover {
    border-color: #8B5CF6;
    color: #8B5CF6;
  }
`;

const SkipButton = styled.button`
  background: #ffffff;
  border: 2px solid #8B5CF6;
  border-radius: 8px;
  padding: 12px 24px;
  color: #8B5CF6;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    background: #F9F5FF;
  }
`;

const NoSegmentsMessage = styled.div`
  text-align: center;
  padding: 32px;
  color: #626262;
  font-size: 14px;
  line-height: 1.6;
`;

interface SegmentationRunSelectorProps {
  onSelectRun: (runId: number, sessionId: string) => void;
  onSelectSegment?: (runId: number, sessionId: string, segmentIdentifier: string) => void;
  prefetchedRuns?: SegmentationRunSummary[] | null;
}

export function SegmentationRunSelector({
  onSelectRun,
  onSelectSegment,
  prefetchedRuns,
}: SegmentationRunSelectorProps): React.JSX.Element {
  const [runs, setRuns] = useState<SegmentationRunSummary[]>(prefetchedRuns || []);
  const [loading, setLoading] = useState(!prefetchedRuns);
  const [selectedRun, setSelectedRun] = useState<SegmentationRunSummary | null>(null);
  const [showingSegments, setShowingSegments] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    // If we have prefetched data, use it immediately
    if (prefetchedRuns) {
      setRuns(prefetchedRuns);
      setLoading(false);
      return;
    }

    // Otherwise fetch
    listSegmentationRuns({ status: 'complete', take: 5 })
      .then((response) => {
        const filtered = response.runs.filter((r) => r.sessionId);
        setRuns(filtered);
      })
      .catch((error) => {
        console.error('[SegmentationRunSelector] Failed to fetch runs:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [prefetchedRuns]);

  const handleRunClick = (run: SegmentationRunSummary) => {
    if (!run.sessionId) return;

    // Always show segment selector to give user the choice
    setSelectedRun(run);
    setShowingSegments(true);
  };

  const handleSkipSegmentSelection = () => {
    if (!selectedRun || !selectedRun.sessionId) return;
    // User chose to proceed with entire run, not a specific segment
    onSelectRun(selectedRun.id, selectedRun.sessionId);
  };

  const handleSegmentClick = (segmentIdentifier: string) => {
    if (!selectedRun || !selectedRun.sessionId || !onSelectSegment) return;
    onSelectSegment(selectedRun.id, selectedRun.sessionId, segmentIdentifier);
  };

  const handleBack = () => {
    setShowingSegments(false);
    setSelectedRun(null);
  };

  if (loading) {
    return (
      <Root>
        <LoadingState>Loading recent segmentation runs...</LoadingState>
      </Root>
    );
  }

  if (showingSegments && selectedRun) {
    const segments = [] as Array<{
      identifier: string;
      name: string;
      size?: number;
    }>;

    return (
      <Root>
        <BackButton onClick={handleBack}>← Back to Runs</BackButton>
        <Header>
          <Title>Select a Segment</Title>
          <Subtitle>
            Choose a specific segment from <strong>{selectedRun.name || 'Untitled Run'}</strong>, or proceed with
            the entire run
          </Subtitle>
        </Header>

        {segments.length > 0 ? (
          <>
            <SegmentsList>
              {segments.map((segment) => (
                <SegmentCard key={segment.identifier} onClick={() => handleSegmentClick(segment.identifier)}>
                  <div>
                    <SegmentName>{segment.name}</SegmentName>
                    {segment.size && <SegmentSize>{segment.size.toLocaleString()} HCPs</SegmentSize>}
                  </div>
                  <ChevronIcon size={16} />
                </SegmentCard>
              ))}
            </SegmentsList>
            <SkipButton onClick={handleSkipSegmentSelection}>
              Use Entire Run (All {segments.length} Segments)
            </SkipButton>
          </>
        ) : (
          <>
            <NoSegmentsMessage>
              This run has no segments saved. You can proceed with the run's overall context.
            </NoSegmentsMessage>
            <SkipButton onClick={handleSkipSegmentSelection}>Proceed with Run</SkipButton>
          </>
        )}
      </Root>
    );
  }

  if (runs.length === 0) {
    return (
      <Root>
        <EmptyState>
          <EmptyTitle>No Segmentation Runs Found</EmptyTitle>
          <EmptyText>
            Complete a segmentation run first, then return here to generate marketing messages.
          </EmptyText>
        </EmptyState>
      </Root>
    );
  }

  return (
    <Root>
      <Header>
        <Title>Select a Segmentation Run</Title>
        <Subtitle>Choose a run to generate marketing messages based on its segments and insights</Subtitle>
      </Header>
      <RunsList>
        {runs.map((run) => (
          <RunCard key={run.id} onClick={() => handleRunClick(run)} disabled={!run.sessionId}>
            <RunIcon>
              <Layers size={24} />
            </RunIcon>
            <RunInfo>
              <RunName>{run.name || `Run ${run.runKey}`}</RunName>
              <RunMeta>
                {run.runTimestamp && (
                  <MetaItem>
                    <Calendar size={14} />
                    {new Date(run.runTimestamp).toLocaleDateString()}
                  </MetaItem>
                )}
                {run.segmentCount && (
                  <MetaItem>
                    <Layers size={14} />
                    {run.segmentCount} segments
                  </MetaItem>
                )}
                {run.totalPatientPopulation && (
                  <MetaItem>
                    <Users size={14} />
                    {run.totalPatientPopulation.toLocaleString()} HCPs
                  </MetaItem>
                )}
              </RunMeta>
            </RunInfo>
            <ChevronIcon size={20} />
          </RunCard>
        ))}
      </RunsList>
    </Root>
  );
}
